import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { studentPhoto } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, Card, EmptyState } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Avatar } from "@/components/Avatar";
import { WhatsNew } from "@/components/WhatsNew";
import { ContactCards } from "@/components/ContactCards";
import { HuddleCard } from "@/components/HuddleCard";
import { BookingReminder } from "@/components/BookingReminder";
import { AceExpertList } from "@/components/AceExperts";
import { ResourceCards } from "@/components/ResourceCards";
import { MyEvents } from "@/components/MyEvents";
import { MyPaperwork } from "@/components/MyPaperwork";
import { SchoolStudentDocs } from "@/components/SchoolStudentDocs";
import { StudentsToName } from "@/components/StudentsToName";
import { MyTouchpoints, AssignedTouchpoints } from "@/components/TouchpointsDue";
import { EventRequests } from "@/components/EventRequests";
import { AwaitingDecisions } from "@/components/AwaitingDecisions";
import { fmtDate } from "@/lib/dates";
import { JobOpportunities } from "@/components/JobOpportunities";
import { StudentTasks } from "@/components/StudentTasks";
import { TaskMeetings } from "@/components/TaskMeetings";
import { StudentProfileView } from "@/pages/StudentProfile";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  GraduationCap, AlertTriangle, Clock, CheckCircle2, FileCheck2, Video,
  Building2, School, Users, Briefcase, FileWarning, CalendarClock, Loader2, ShieldCheck, MessageSquare, ListTodo, Megaphone, Printer
} from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const reload = () => api.get("/dashboard").then(({ data }) => setData(data));
  useEffect(() => { reload(); }, []);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-cc-blue" /></div>;

  const role = user.role;
  const greeting = `Welcome back, ${user.name.split(" ")[0]}`;

  return (
    <div>
      <PageHeader title={greeting} subtitle={`${role} · here's what needs your attention`} />

      <WhatsNew role={role} />

      {(role === "Super Admin" || role === "Campus Coordinator" || role === "Campus Coordinator Supervisor") && <CoordinatorDash data={data} nav={navigate} onChanged={reload} />}
      {role === "Campus Coordinator Supervisor" && <SupervisorDash nav={navigate} />}
      {role === "School User" && <SchoolDash data={data} nav={navigate} reload={reload} />}
      {role === "Student" && <StudentDash data={data} nav={navigate} onChanged={reload} />}
      {role === "Clinical Instructor" && (data.pending_requests !== undefined ? <FacilityDash data={data} nav={navigate} /> : <CIDash data={data} nav={navigate} />)}
      {role === "DOR" && <FacilityDash data={data} nav={navigate} />}
      {role === "Recruiter" && <RecruiterDash data={data} nav={navigate} />}
      {role === "Area Manager" && <AreaManagerDash data={data} nav={navigate} />}
    </div>
  );
}

function SupervisorDash({ nav }) {
  const [data, setData] = useState(null);
  const [gapFor, setGapFor] = useState(null); // {id, name} or {id:"unassigned"} or {id:"all"}
  const [gapStudents, setGapStudents] = useState(null);
  useEffect(() => { api.get("/supervisor-overview").then(({ data }) => setData(data)); }, []);
  const openGaps = (cc) => {
    setGapFor(cc); setGapStudents(null);
    api.get(`/supervisor-overview/gaps`, { params: cc.id === "all" ? {} : { cc_id: cc.id || "unassigned" } })
      .then(({ data }) => setGapStudents(data));
  };
  if (!data) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-cc-blue" /></div>;
  const { coordinators, totals } = data;
  return (
    <div className="space-y-4 mt-6" data-testid="supervisor-dash">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-cc-blue" />
        <h2 className="text-lg font-semibold text-zinc-800">Coordinator Oversight</h2>
        <span className="text-xs text-zinc-400">All coordinators at a glance</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label="Coordinators" value={totals.coordinators} icon={Users} tone="blue" testId="sup-coordinators" />
        <StatCard label="Open Requests" value={totals.open_requests} icon={GraduationCap} tone="orange" onClick={() => nav("/placements")} testId="sup-open" />
        <StatCard label="Awaiting Response" value={totals.awaiting_response} icon={Clock} tone="orange" testId="sup-awaiting" />
        <StatCard label="Touchpoint Gaps" value={totals.touchpoint_gaps} icon={AlertTriangle} tone="red" onClick={() => openGaps({ id: "all", name: "All coordinators" })} testId="sup-gaps" />
        <StatCard label="Event Requests" value={totals.pending_event_requests} icon={CalendarClock} tone="orange" onClick={() => nav("/engagement")} testId="sup-eventreq" />
      </div>
      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto cc-scroll">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase">
              <tr>
                <th className="text-left px-5 py-3">Coordinator</th>
                <th className="text-left px-5 py-3">Open Requests</th>
                <th className="text-left px-5 py-3">Awaiting</th>
                <th className="text-left px-5 py-3">Non-Placement</th>
                <th className="text-left px-5 py-3">Students</th>
                <th className="text-left px-5 py-3">Touchpoint Gaps</th>
              </tr>
            </thead>
            <tbody>
              {coordinators.map((c) => (
                <tr key={c.id || "unassigned"} className="border-t" data-testid={`sup-row-${c.id || "unassigned"}`}>
                  <td className="px-5 py-3 font-medium text-zinc-800">{c.name}{c.role === "Campus Coordinator Supervisor" && <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-cc-blue/10 text-cc-blue rounded">Supervisor</span>}<div className="text-xs text-zinc-400">{c.email}</div></td>
                  <td className="px-5 py-3">{c.open_requests}</td>
                  <td className="px-5 py-3">{c.awaiting_response}</td>
                  <td className="px-5 py-3">{c.non_placement > 0 ? <span className="text-red-600 font-semibold">{c.non_placement}</span> : c.non_placement}</td>
                  <td className="px-5 py-3">{c.students}</td>
                  <td className="px-5 py-3">
                    {c.touchpoint_gaps > 0 ? (
                      <button className="inline-flex items-center gap-1 text-red-600 font-semibold hover:underline" onClick={() => openGaps(c)} data-testid={`sup-gaps-link-${c.id || "unassigned"}`}>
                        <AlertTriangle className="w-3.5 h-3.5" />{c.touchpoint_gaps}
                      </button>
                    ) : <span className="text-zinc-400">0</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!gapFor} onOpenChange={(o) => !o && setGapFor(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto cc-scroll" data-testid="gap-drilldown-dialog">
          <DialogHeader>
            <DialogTitle>Touchpoint gaps — {gapFor?.name}</DialogTitle>
            <DialogDescription>Students with overdue rotation or graduation touchpoints. Click one to open their profile.</DialogDescription>
          </DialogHeader>
          {gapStudents === null ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-cc-blue" /></div> :
            gapStudents.length === 0 ? <EmptyState text="No students with overdue touchpoints." /> : (
            <div className="space-y-2">
              {gapStudents.map((s) => (
                <button key={s.id} className="w-full text-left border rounded-md p-3 hover:border-cc-blue transition-colors" onClick={() => { setGapFor(null); nav(`/students/${s.id}`); }} data-testid={`gap-student-${s.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm text-zinc-800">{s.name}{s.discipline ? ` · ${s.discipline}` : ""}</span>
                    <span className="text-xs text-zinc-400">{s.state || "—"}</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {s.gaps.map((g) => <span key={g} className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-600 border border-red-200 rounded">{g}</span>)}
                  </div>
                  {s.graduation_date && <p className="text-xs text-zinc-400 mt-1">Grad {fmtDate(s.graduation_date)}</p>}
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CoordinatorDash({ data, nav, onChanged }) {
  return (
    <div className="space-y-6">
      {data.task_meetings_awaiting_me > 0 && (
        <div className="border-l-4 border-cc-orange bg-orange-50 rounded-md px-5 py-4 flex items-center justify-between" data-testid="task-meeting-banner">
          <div className="flex items-center gap-3">
            <CalendarClock className="w-5 h-5 text-cc-orange" />
            <div>
              <p className="font-semibold text-orange-900">{data.task_meetings_awaiting_me} student meeting{data.task_meetings_awaiting_me > 1 ? "s" : ""} waiting on you</p>
              <p className="text-sm text-orange-700">Confirm the time they picked, or propose another.</p>
            </div>
          </div>
        </div>
      )}
      {data.non_placement_alerts > 0 && (
        <div className="border-l-4 border-red-500 bg-red-50 rounded-md px-5 py-4 flex items-center justify-between cc-urgent" data-testid="non-placement-banner">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <div>
              <p className="font-semibold text-red-800">{data.non_placement_alerts} Non-Placement Alert{data.non_placement_alerts > 1 ? "s" : ""}</p>
              <p className="text-sm text-red-600">Students with no facility acceptance past the deadline. Action required.</p>
            </div>
          </div>
          <Button onClick={() => nav("/placements")} className="bg-red-600 hover:bg-red-700 text-white" data-testid="view-alerts-btn">Review</Button>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Requests" value={data.total_requests} icon={GraduationCap} tone="blue" onClick={() => nav("/placements")} testId="stat-total" />
        <StatCard label="Awaiting Response" value={data.awaiting_response} icon={Clock} tone="orange" onClick={() => nav("/placements")} testId="stat-awaiting" />
        <StatCard label="Non-Placement" value={data.non_placement_alerts} icon={AlertTriangle} tone="red" onClick={() => nav("/placements")} testId="stat-nonplacement" />
        <StatCard label="Active Placements" value={data.active_placements} icon={CheckCircle2} tone="green" testId="stat-active" />
        <StatCard label="Not Cleared" value={data.not_cleared} icon={FileWarning} tone="red" onClick={() => nav("/paperwork")} testId="stat-notcleared" />
        <StatCard label="Paperwork to Review" value={data.paperwork_pending} icon={FileCheck2} tone="orange" onClick={() => nav("/paperwork")} testId="stat-paperwork" />
        <StatCard label="Starts Soon, Not Cleared" value={data.starts_soon_not_cleared} icon={CalendarClock} tone="red" testId="stat-startssoon" />
        <StatCard label="Agreements Expiring" value={data.agreements_expiring} icon={FileWarning} tone="orange" onClick={() => nav("/schools")} testId="stat-agreements" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Schools" value={data.schools} icon={School} onClick={() => nav("/schools")} />
        <StatCard label="Facilities" value={data.facilities} icon={Building2} onClick={() => nav("/facilities")} />
        <StatCard label="Students" value={data.students} icon={Users} onClick={() => nav("/students")} />
        <StatCard label="Upcoming Video Calls" value={data.video_upcoming} icon={Video} onClick={() => nav("/videos")} />
        <StatCard label="Student Tasks Outstanding" value={data.student_tasks_outstanding} icon={ListTodo} onClick={() => nav("/student-tasks")} testId="stat-student-tasks" />
      </div>
      <AwaitingDecisions />
      <EventRequests />
      <TaskMeetings meetings={data.task_meetings} onChanged={onChanged} />
      <AssignedTouchpoints onChanged={onChanged} />
    </div>
  );
}

function SchoolStateBoard({ data, nav }) {
  const groups = data.by_state || [];
  const [open, setOpen] = useState(groups[0]?.state || null);
  if (groups.length === 0) return <Card title="Your students by state"><EmptyState text="No placement requests yet — use “+ Initiate Student Request” to send your first one." /></Card>;
  const Bucket = ({ title, rows, tone, showDetail }) => (
    <div className="min-w-0">
      <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${tone}`}>{title} ({rows.length})</p>
      {rows.length === 0 ? <p className="text-xs text-zinc-400">None</p> : (
        <div className="space-y-2">
          {rows.map((r) => (
            <button key={r.pr_id} onClick={() => nav(`/placements/${r.pr_id}`)}
              className="w-full text-left border rounded-md px-3 py-2 bg-white hover:border-cc-blue transition-colors"
              data-testid={`school-row-${r.pr_id}`}>
              <div className="flex items-center gap-2.5">
                <Avatar src={studentPhoto(r.student_id)} name={r.student_name} size={26} />
                <span className="font-medium text-sm text-zinc-800 truncate">{r.student_name || "Unnamed student"}</span>
                <span className="text-xs text-zinc-400">{r.discipline}</span>
              </div>
              {showDetail ? (
                <div className="mt-2 grid gap-y-1 text-xs text-zinc-600">
                  <div><span className="text-zinc-400">Rotation:</span> {fmt(r.rotation_start)} → {fmt(r.rotation_end)}</div>
                  <div><span className="text-zinc-400">Facility:</span> {r.facility_name || "—"}</div>
                  <div><span className="text-zinc-400">CI:</span> {r.ci_name || "Not assigned yet"}{r.ci_email ? ` · ${r.ci_email}` : ""}</div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-zinc-400">Documents:</span>
                    {r.docs_required === 0 ? <span className="text-zinc-400">not assigned yet</span> : (
                      <span className={r.docs_complete ? "text-cc-green font-medium" : "text-amber-600 font-medium"}>
                        {r.docs_in} of {r.docs_required} in{r.cleared ? " · cleared to start" : ""}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mt-1.5 text-xs text-zinc-500">{r.placement_status || "Submitted"} · requested {fmt(r.rotation_start)}</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
  return (
    <Card title="Your students by state" data-testid="school-state-board">
      <p className="text-sm text-zinc-500 mb-4">Grouped by the state the student is being placed in. Open a student for the full placement record.</p>
      <div className="space-y-2">
        {groups.map((g) => {
          const isOpen = open === g.state;
          return (
            <div key={g.state} className="border rounded-lg" data-testid={`school-state-${g.state}`}>
              <button onClick={() => setOpen(isOpen ? null : g.state)} className="w-full flex flex-wrap items-center gap-3 px-4 py-3 text-left">
                <span className="font-semibold text-zinc-900 w-10">{g.state}</span>
                <span className="ml-auto flex items-center gap-4 text-sm">
                  <span className="text-amber-600" data-testid={`school-pending-${g.state}`}>{g.pending.length} pending</span>
                  <span className="text-cc-green" data-testid={`school-yes-${g.state}`}>{g.yes.length} yes</span>
                  <span className="text-zinc-500" data-testid={`school-no-${g.state}`}>{g.no.length} no</span>
                </span>
              </button>
              {isOpen && (
                <div className="px-4 pb-4 grid md:grid-cols-3 gap-5">
                  <Bucket title="Pending decision" rows={g.pending} tone="text-amber-600" />
                  <Bucket title="Yes — confirmed" rows={g.yes} tone="text-cc-green" showDetail />
                  <Bucket title="No" rows={g.no} tone="text-zinc-500" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function SchoolDash({ data, nav, reload }) {
  return (
    <div className="space-y-6">
      <StudentsToName rows={data.students_to_name} onSaved={reload} />
      <div className="flex flex-wrap justify-end gap-3">
        <Button variant="outline" onClick={() => nav("/engagement")} data-testid="school-request-event-btn"><Megaphone className="w-4 h-4 mr-1.5" />Request an Event</Button>
        <Button onClick={() => nav("/placements?new=1")} className="bg-cc-blue hover:bg-cc-blue-dark text-white" data-testid="initiate-request-btn">+ Initiate Student Request</Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Requests" value={data.total_requests} icon={GraduationCap} tone="blue" onClick={() => nav("/placements")} />
        <StatCard label="Awaiting Placement" value={data.awaiting_placement} icon={Clock} tone="orange" />
        <StatCard label="Confirmed" value={data.confirmed} icon={CheckCircle2} tone="green" />
        <StatCard label="Not Cleared" value={data.not_cleared} icon={FileWarning} tone="red" />
        <StatCard label="Current Students" value={data.students} icon={Users} onClick={() => nav("/students")} />
      </div>
      <SchoolStateBoard data={data} nav={nav} />
      <SchoolStudentDocs />
      <MyEvents events={data.my_events} />
      <ResourceCards resources={data.resources} title="Resources for your school" />
      <ContactCards contacts={data.contacts} title="Your Campus Coordinators" testId="school-contacts"
        hint="You may work with more than one coordinator when your students are placed across states. Click a name to read their bio." />
    </div>
  );
}

function StudentDash({ data, nav, onChanged }) {
  const w = data.welcome_message;
  return (
    <div className="space-y-6">
      {w && (
        <div className="rounded-xl border border-cc-blue/25 bg-cc-blue/[0.04] px-6 py-5" data-testid="student-welcome-card">
          <h2 className="text-lg font-semibold text-zinc-900" data-testid="student-welcome-heading">{w.heading}</h2>
          {w.paragraphs.map((p, i) => (
            <p key={i} className="text-sm text-zinc-600 mt-2 leading-relaxed" data-testid={`student-welcome-p-${i}`}>{p}</p>
          ))}
        </div>
      )}
      {data.video_meetings?.some((v) => v.status === "Started") && (
        <div className="border-l-4 border-cc-orange bg-orange-50 rounded-md px-5 py-4 flex items-center justify-between cc-flash" data-testid="video-starting-banner">
          <div className="flex items-center gap-3">
            <Video className="w-5 h-5 text-cc-orange" />
            <p className="font-semibold text-orange-800">Your coordinator started a video call!</p>
          </div>
          <Button onClick={() => nav("/videos")} className="bg-cc-orange hover:bg-orange-600 text-white" data-testid="join-call-btn">Join now</Button>
        </div>
      )}
      <div className="flex flex-wrap gap-3">
        <Button onClick={() => nav("/videos?request=1")} className="bg-cc-green hover:bg-green-600 text-white" data-testid="request-video-btn">Request a video call with my coordinator</Button>
        <Button variant="outline" onClick={() => nav("/paperwork")} data-testid="my-paperwork-btn">Manage paperwork</Button>
        <a href="/student-welcome-guide.html" target="_blank" rel="noreferrer">
          <Button variant="outline" data-testid="student-welcome-guide-btn"><Printer className="w-4 h-4 mr-1.5" />Print my welcome guide</Button>
        </a>
      </div>
      <BookingReminder alerts={data.booking_alerts} />
      <HuddleCard huddle={data.huddle} />
      <MyPaperwork />
      <MyTouchpoints />
      <StudentTasks onChanged={onChanged} />
      <JobOpportunities jobs={data.jobs} onChanged={onChanged} />
      <ResourceCards resources={data.resources} title="Videos & resources for you" />
      <ContactCards contacts={data.contacts} title="Your people" testId="student-contacts"
        hint="Your Clinical Instructor, Campus Coordinator and school contact. Click a name to read their bio." />
      {data.student?.id ? <StudentProfileView studentId={data.student.id} embedded /> : <EmptyState text="No student profile yet. Your school will initiate one." />}
    </div>
  );
}

function AwaitingCICard({ data, nav }) {
  const awaiting = data.awaiting_ci || [];
  const cis = data.facility_cis || [];
  const [picks, setPicks] = useState({});
  if (awaiting.length === 0) return null;
  const assign = async (prId) => {
    const ci = picks[prId];
    if (!ci) return toast.error("Pick a Clinical Instructor first");
    try { await api.post(`/placement-requests/${prId}/assign-ci`, { clinical_instructor_id: ci }); toast.success("Clinical Instructor assigned"); window.location.reload(); }
    catch (e) { toast.error("Could not assign — try again"); }
  };
  return (
    <Card className="border-l-4 border-cc-orange" title="Assign a Clinical Instructor" data-testid="awaiting-ci-card">
      <p className="text-sm text-zinc-500 mb-3">You accepted these students but haven't picked a Clinical Instructor yet. Assign one so the student knows who they'll work with.</p>
      <div className="space-y-2">
        {awaiting.map((a) => (
          <div key={a.pr_id} className="flex flex-wrap items-center gap-3 border rounded-md px-3 py-2" data-testid={`awaiting-ci-${a.pr_id}`}>
            <Avatar src={studentPhoto(a.student_id)} name={a.student_name} size={28} />
            <span className="font-medium text-zinc-800">{a.student_name}</span>
            <span className="text-xs text-zinc-400">{a.discipline}</span>
            <div className="ml-auto flex items-center gap-2">
              <Select value={picks[a.pr_id] || ""} onValueChange={(v) => setPicks({ ...picks, [a.pr_id]: v })}>
                <SelectTrigger className="w-56 h-9" data-testid={`awaiting-ci-select-${a.pr_id}`}><SelectValue placeholder="Choose a Clinical Instructor" /></SelectTrigger>
                <SelectContent>{cis.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.role_label ? ` (${c.role_label})` : ""}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" className="bg-cc-blue text-white" onClick={() => assign(a.pr_id)} data-testid={`awaiting-ci-assign-${a.pr_id}`}>Assign</Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function FacilityDash({ data, nav }) {
  const details = data.pending_request_details || [];
  const coords = data.my_coordinators || [];
  return (
    <div className="space-y-6">
      {coords.length > 0 && (
        <Card className="border-l-4 border-cc-blue" title={`Your Campus Coordinator${coords.length > 1 ? "s" : ""}${data.facility?.state ? ` — ${data.facility.state}` : ""}`} data-testid="dor-coordinators">
          <div className="flex flex-wrap gap-3">
            {coords.map((c) => (
              <div key={c.email} className="flex flex-wrap items-center gap-3 border rounded-lg px-3.5 py-2.5 bg-white" data-testid={`dor-coordinator-${c.email}`}>
                <div>
                  <p className="text-sm font-semibold text-zinc-900">{c.name}</p>
                  <p className="text-xs text-zinc-500">{c.email}</p>
                </div>
                <Button size="sm" variant="outline" className="h-8" onClick={() => nav("/messages")} data-testid={`dor-message-${c.email}`}>
                  <MessageSquare className="w-4 h-4 mr-1.5 text-cc-blue" />Message
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Pending Requests" value={data.pending_requests} icon={Clock} tone="orange" onClick={() => nav("/placements")} testId="stat-pending" />
        <StatCard label="Answered Yes" value={data.accepted} icon={CheckCircle2} tone="green" />
        <StatCard label="Answered No" value={data.denied} icon={AlertTriangle} />
        <StatCard label="Clinical Instructors" value={data.clinical_instructors} icon={Users} onClick={() => nav("/clinical-instructors")} />
      </div>

      {data.is_preceptor && (
        <MyStudentsCard students={(data.my_students || []).filter((s) => s.mine_as_ci)} nav={nav}
          testId="dor-ci-student" title={`Students you precept as Clinical Instructor (${data.my_ci_student_count || 0})`}
          hint="You're the DOR and the Clinical Instructor for these students — both jobs are on this one dashboard."
          empty="No students are assigned to you as Clinical Instructor yet." />
      )}

      <AwaitingCICard data={data} nav={nav} />

      {data.facility && (
        <Card title={data.facility.name || "Your facility"} data-testid="facility-info-card">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <Info label="Location" value={[data.facility.city, data.facility.state].filter(Boolean).join(", ")} />
            <Info label="Director of Rehab" value={data.facility.dor_name} />
            <Info label="Area Manager" value={data.facility.am_name} />
            <Info label="Regional Director" value={data.facility.rdo_name} />
          </div>
        </Card>
      )}

      <ContactCards contacts={data.contacts} title="Your campus team & instructors" testId="facility-contacts"
        hint="Coordinators covering your state and the Clinical Instructors at your site. Click a name to read their bio." />


      <Card title="Requests awaiting your Yes / No">
        {details.length === 0 ? <EmptyState text="No pending requests right now." /> : (
          <div className="overflow-x-auto cc-scroll -m-5">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-5 py-3">Student</th>
                  <th className="text-left px-5 py-3">School / Contact</th>
                  <th className="text-left px-5 py-3">Coordinator</th>
                  <th className="text-left px-5 py-3">Rotation</th>
                  <th className="text-left px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {details.map((d) => (
                  <tr key={d.pr_id} className="border-t align-top" data-testid={`dor-request-${d.pr_id}`}>
                    <td className="px-5 py-3"><div className="flex items-center gap-2.5"><Avatar src={studentPhoto(d.student_id)} name={d.student_name} size={30} testId={`dor-avatar-${d.pr_id}`} /><div><div className="font-medium text-zinc-800">{d.student_name || "—"}</div>{d.discipline && <div className="text-xs text-zinc-400">{d.discipline}</div>}</div></div></td>
                    <td className="px-5 py-3"><div className="text-zinc-700">{d.school_name || "—"}</div>{d.school_contact_name && <div className="text-xs text-zinc-400">{d.school_contact_name}{d.school_contact_email ? ` · ${d.school_contact_email}` : ""}</div>}</td>
                    <td className="px-5 py-3">{d.coordinator_name ? <><div className="text-zinc-700">{d.coordinator_name}</div><div className="text-xs text-zinc-400">{d.coordinator_email}</div></> : <span className="text-zinc-400">—</span>}</td>
                    <td className="px-5 py-3 text-xs font-mono">{fmt(d.rotation_start)} → {fmt(d.rotation_end)}</td>
                    <td className="px-5 py-3"><Button size="sm" className="h-7 text-xs bg-cc-green text-white" onClick={() => nav(`/placements/${d.pr_id}`)} data-testid={`dor-review-${d.pr_id}`}>Mark Yes / No</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <AnsweredCard yes={data.answered_yes} no={data.answered_no} nav={nav} />
      <AceExpertList experts={data.ace_experts} />
      <ResourceCards resources={data.resources} title="Resources for your team" />
    </div>
  );
}

function AnsweredCard({ yes, no, nav }) {
  const [tab, setTab] = useState("yes");
  const rows = (tab === "yes" ? yes : no) || [];
  return (
    <Card title="Everything you've answered" data-testid="dor-answered-card">
      <div className="flex gap-2 mb-4">
        {[["yes", `Yes (${(yes || []).length})`], ["no", `No (${(no || []).length})`]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`dor-answered-tab-${k}`}
            className={`px-3 py-1.5 rounded-md text-sm border ${tab === k ? "bg-cc-blue text-white border-cc-blue" : "bg-white text-zinc-600 hover:border-cc-blue"}`}>{label}</button>
        ))}
      </div>
      {rows.length === 0 ? <EmptyState text={tab === "yes" ? "You haven't accepted any students yet." : "You haven't declined any requests."} /> : (
        <div className="overflow-x-auto cc-scroll -m-5">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase">
              <tr>
                <th className="text-left px-5 py-3">Student</th>
                <th className="text-left px-5 py-3">School</th>
                <th className="text-left px-5 py-3">Rotation</th>
                <th className="text-left px-5 py-3">{tab === "yes" ? "Clinical Instructor" : "Reason"}</th>
                {tab === "yes" && <th className="text-left px-5 py-3">Status</th>}
                <th className="text-left px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.pr_id} className="border-t align-top" data-testid={`dor-answered-${r.pr_id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2.5">
                      <Avatar src={studentPhoto(r.student_id)} name={r.student_name} size={28} />
                      <div><div className="font-medium text-zinc-800">{r.student_name || "—"}</div><div className="text-xs text-zinc-400">{r.discipline}</div></div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-zinc-600">{r.school_name || "—"}</td>
                  <td className="px-5 py-3 text-xs font-mono">{fmt(r.rotation_start)} → {fmt(r.rotation_end)}</td>
                  <td className="px-5 py-3 text-zinc-600">
                    {tab === "yes"
                      ? (r.ci_name ? <><div>{r.ci_name}</div><div className="text-xs text-zinc-400">{r.ci_email}</div></> : <span className="text-amber-600 text-xs">Not assigned yet</span>)
                      : (r.reason || <span className="text-zinc-400">—</span>)}
                  </td>
                  {tab === "yes" && (
                    <td className="px-5 py-3 text-xs">
                      {r.cleared
                        ? <span className="text-cc-green font-medium">All clear</span>
                        : <span className="text-zinc-400">awaiting campus clearance</span>}
                    </td>
                  )}
                  <td className="px-5 py-3"><Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => nav(`/placements/${r.pr_id}`)} data-testid={`dor-open-${r.pr_id}`}>Open</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function MyStudentsCard({ students, nav, title, empty, testId = "ci-student", hint }) {
  return (
    <Card title={title} data-testid={`${testId}s-card`}>
      {hint && <p className="text-sm text-zinc-500 mb-3">{hint}</p>}
      {(students || []).length === 0 ? <EmptyState text={empty} /> : (
        <div className="overflow-x-auto cc-scroll -m-5">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase">
              <tr>
                <th className="text-left px-5 py-3">Student</th>
                <th className="text-left px-5 py-3">School &amp; contact</th>
                <th className="text-left px-5 py-3">Rotation</th>
                <th className="text-left px-5 py-3">Program documents</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {students.map((s) => (
                <tr key={s.pr_id} className="border-t" data-testid={`${testId}-${s.pr_id}`}>
                  <td className="px-5 py-3"><div className="flex items-center gap-2.5"><Avatar src={studentPhoto(s.student_id)} name={s.student_name} size={30} testId={`myst-avatar-${s.pr_id}`} /><div><div className="font-medium text-zinc-800">{s.student_name || "—"}</div>{s.discipline && <div className="text-xs text-zinc-400">{s.discipline}</div>}</div></div></td>
                  <td className="px-5 py-3 text-zinc-600">
                    <div>{s.school_name || "—"}</div>
                    {s.school_contact_name && (
                      <div className="text-xs text-zinc-400">{s.school_contact_name}{s.school_contact_email ? <> · <a href={`mailto:${s.school_contact_email}`} className="hover:text-cc-blue">{s.school_contact_email}</a></> : ""}</div>
                    )}
                  </td>
                  <td className="px-5 py-3 text-xs font-mono">{fmt(s.rotation_start)} → {fmt(s.rotation_end)}</td>
                  <td className="px-5 py-3">
                    {(s.program_documents || []).length === 0 ? <span className="text-xs text-zinc-400">None shared</span> : (
                      <div className="space-y-0.5">
                        {s.program_documents.map((d) => (
                          <a key={d.id} href={d.url} target="_blank" rel="noreferrer" className="block text-xs text-cc-blue hover:underline" data-testid={`ci-progdoc-${d.id}`}>{d.title}</a>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <StatusBadge status={s.status_display || s.status} />
                    {s.docs_required > 0 && !s.paperwork_complete && (
                      <div className="text-[11px] text-zinc-400 mt-1">{s.docs_in}/{s.docs_required} documents in</div>
                    )}
                  </td>
                  <td className="px-5 py-3"><Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => nav(`/placements/${s.pr_id}`)} data-testid={`ci-view-${s.pr_id}`}>View</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function CIDash({ data, nav }) {
  const students = data.my_students || [];
  const byState = data.coordinators_by_state || [];
  return (
    <div className="space-y-6">
      {byState.length > 0 && (
        <Card className="border-l-4 border-cc-blue" title={byState.length === 1 && byState[0].coordinators.length === 1 ? "Your Campus Coordinator" : "Your Campus Coordinators"} data-testid="ci-coordinators-by-state">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {byState.map((g) => (
              <div key={g.state} className="border rounded-lg px-3 py-2.5" data-testid={`ci-cc-state-${g.state}`}>
                <p className="text-[10px] font-bold uppercase tracking-[0.07em] text-cc-blue">{g.state}</p>
                {g.coordinators.map((c) => (
                  <div key={c.id} className="mt-1">
                    <p className="text-sm font-medium text-zinc-800">{c.name}</p>
                    <a href={`mailto:${c.email}`} className="text-xs text-zinc-500 hover:text-cc-blue">{c.email}</a>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <Button size="sm" variant="outline" className="mt-3" onClick={() => nav("/messages")} data-testid="ci-message-cc">
            <MessageSquare className="w-4 h-4 mr-1.5 text-cc-blue" />Send a message
          </Button>
        </Card>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="My Students" value={data.assigned_students} icon={Users} tone="blue" />
        <StatCard label="Active Now" value={data.current_students} icon={GraduationCap} tone="green" />
      </div>
      <ContactCards contacts={data.contacts} title="Your people" testId="ci-contacts"
        hint="Your DOR and the Campus Coordinators for your students. Click a name to read their bio." />
      <AceExpertList experts={data.ace_experts} />
      <MyStudentsCard students={students} nav={nav} title="My Students" empty="No students are assigned to you yet." testId="ci-student" />
      <ResourceCards resources={data.resources} title="Clinical Instructor resources" />
    </div>
  );
}

function AreaManagerDash({ data, nav }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="My Sites" value={data.my_facilities} icon={Building2} tone="blue" onClick={() => nav("/reports")} testId="am-stat-sites" />
        <StatCard label="Pending Decisions" value={data.pending} icon={Clock} tone="orange" onClick={() => nav("/reports")} />
        <StatCard label="Answered Yes" value={data.yes} icon={CheckCircle2} tone="green" />
        <StatCard label="Answered No" value={data.no} icon={AlertTriangle} />
        <StatCard label="Cleared to Start" value={data.cleared} icon={FileCheck2} tone="green" />
        <StatCard label="Not Yet Cleared" value={data.not_cleared} icon={FileWarning} tone="red" />
      </div>
      <Card title="Your states" data-testid="am-states-card">
        {(data.my_states || []).length === 0 ? (
          <EmptyState text="No sites are assigned to you yet — ask your Campus Coordinator to add your facilities or states." />
        ) : (
          <div className="flex flex-wrap gap-2">
            {data.my_states.map((s) => (
              <span key={s} className="px-2.5 py-1 rounded-md bg-cc-blue/10 text-cc-blue text-sm font-medium" data-testid={`am-state-chip-${s}`}>{s}</span>
            ))}
          </div>
        )}
      </Card>
      <Button onClick={() => nav("/reports")} className="bg-cc-blue text-white" data-testid="am-open-reports">Open full report</Button>
    </div>
  );
}

function RecruiterDash({ data, nav }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <StatCard label="Referrals" value={data.referrals} icon={Briefcase} tone="blue" onClick={() => nav("/recruiting")} />
      <StatCard label="Interested" value={data.interested} icon={Users} tone="orange" onClick={() => nav("/recruiting")} />
      <StatCard label="In Pipeline" value={data.in_pipeline} icon={Clock} />
      <StatCard label="Hired" value={data.hired} icon={CheckCircle2} tone="green" />
    </div>
  );
}

function Info({ label, value }) {
  return <div><div className="text-xs uppercase tracking-wide text-zinc-400">{label}</div><div className="text-zinc-800">{value || "—"}</div></div>;
}
const fmt = fmtDate;
